import { useState, useEffect } from 'react';
import produce from 'immer';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { get, post, put } from 'helpers/apiHelpers';
import { trimSpaces } from 'helpers/helpers';
import FormControlButtons from 'components/FormControlButtons/FormControlButtons';
import BasicInfoCard from 'components/CMS/BasicInfoCard';
import DescriptionCard from 'components/CMS/DescriptionCard';
import NavTabs from 'components/NavTabs/NavTabs';
import { toast } from 'react-toastify';
import { useGetCompanyDefaultLanguage } from 'hooks/redux/Translatable/useTranslatableLanguages';

import { ADDON_TYPES } from './addonTypes';
import ClientPanel from './ClientPanel';

const initialFormData = {
  active: true,
  availableInShop: true,
  category: null,
  clientCost: 0,
  clientVat: 0,
  code: '',
  contractor: null,
  cost: 0,
  defaultUsage: 'FOR_BAG',
  excludedPlaces: [],
  restrictedToDiets: [],
  image: null,
  imageUrl: null,
  name: '',
  notes: '',
  position: 0,
  shortDescription: '',
  stock: 0,
  type: ADDON_TYPES.ADDON,
  requiredAddon: null,
  volume: 0,
  weight: 0,
};

const getSchema = companyDefaultLanguage =>
  yup.object().shape({
    clientCost: yup.number().required(),
    clientVat: yup.number().required(),
    code: yup.string().required(),
    name: yup
      .object({
        '@ContentTranslation': yup.object().test({
          test: value => value && value[companyDefaultLanguage],
        }),
      })
      .required(),
  });

const getHydraMembers = response => response['hydra:member'] || [];

const AddonsForm = ({ history, location, match }) => {
  const companyDefaultLanguage = useGetCompanyDefaultLanguage();
  const isEdit = location.pathname.includes('edit');
  const addonId = match.params.id;
  const { t } = useTranslation();

  const [formData, setFormData] = useState(initialFormData);
  const [categories, setCategories] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [diets, setDiets] = useState([]);
  const [subAddons, setSubAddons] = useState([]);
  const [addonSubpageData, setAddonSubpageData] = useState({
    urlSlug: '',
    metaTitle: '',
    metaDescription: '',
  });

  const [addonDescription, setAddonDescription] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      const noContractorOption = {
        '@id': null,
        name: t('form.field.contractor.selectOption.noContractor'),
      };

      try {
        const response = await get('/addon-categories', { pagination: false });
        setCategories(getHydraMembers(response));
      } catch {
        setCategories([]);
      }
      try {
        const response = await get('/diets', { pagination: false });
        setDiets(getHydraMembers(response));
      } catch {
        setDiets([]);
      }

      try {
        const response = await get('/contractors', { pagination: false });
        setContractors([noContractorOption, ...getHydraMembers(response)]);
      } catch {
        setContractors([noContractorOption]);
      }

      try {
        const response = await get('/addons', {
          pagination: false,
          translations: true,
        });
        const subAddonsOptions = (response['hydra:member'] || [])
          .filter(addon => addon.type === ADDON_TYPES.SUB_ADDON)
          .map(addon => ({
            '@id': addon['@id'],
            name:
              addon?.name?.['@ContentTranslation']?.[companyDefaultLanguage] ||
              (typeof addon?.name === 'string' ? addon.name : null) ||
              addon?.code ||
              '-',
          }));

        setSubAddons(subAddonsOptions);
      } catch {
        setSubAddons([]);
      }

      if (isEdit) {
        try {
          const response = await get(`/addons/${addonId}`, {
            translations: true,
          });
          const {
            '@id': iriId,
            '@type': iriType,
            createdAt,
            updatedAt,
            category,
            image,
            type,
            requiredAddons,
            ...onlyFormData
          } = response;

          setFormData({
            ...onlyFormData,
            category: category?.['@id'] || null,
            image: image?.['@id'] || null,
            imageUrl: image?.contentUrl || null,
            type: type || ADDON_TYPES.ADDON,
            requiredAddon: requiredAddons[0] || null,
          });

          setAddonSubpageData({
            urlSlug: response.pageSettings.slug ?? '',
            metaTitle: response.seo.title ?? '',
            metaDescription: response.seo.description ?? '',
          });
          setAddonDescription(response.pageSettings.content ?? '');
          // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-empty -- FIXME
        } catch (error) {}
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, []);

  const handleChange = ({ target: { name, value } }) => {
    setFormData(
      produce(draft => {
        draft[name] = value;

        if (name === 'type' && value === ADDON_TYPES.SUB_ADDON) {
          draft.requiredAddon = null;
          draft.shortDescription = initialFormData.shortDescription;
          draft.active = initialFormData.active;
          draft.excludedPlaces = initialFormData.excludedPlaces;
          draft.defaultUsage = initialFormData.defaultUsage;
          draft.category = initialFormData.category;
          draft.position = initialFormData.position;
          draft.restrictedToDiets = initialFormData.restrictedToDiets;
          draft.availableInShop = initialFormData.availableInShop;
        }
      })
    );
  };

  const handleAddonSubpageInputChange = e => {
    setAddonSubpageData(prevData => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));
  };

  const handleCheckboxClick = ({ target: { name } }) => {
    setFormData(
      produce(draft => {
        draft[name] = !draft[name];
      })
    );
  };

  const getImage = (stateName, data) => {
    setFormData(
      produce(draft => {
        draft[stateName] = data?.['@id'] || null;
        draft[`${stateName}Url`] = data?.contentUrl || null;
      })
    );
  };

  const removeImage = stateName => {
    setFormData(
      produce(draft => {
        draft[stateName] = null;
        draft[`${stateName}Url`] = null;
      })
    );
  };

  const addonType = formData.type || ADDON_TYPES.ADDON;
  const isSubAddon = addonType === ADDON_TYPES.SUB_ADDON;
  const canEditClientFields = !isSubAddon;
  const canSelectRequiredAddons = addonType === ADDON_TYPES.ADDON;

  // eslint-disable-next-line consistent-return -- FIXME
  const handleSubmit = async () => {
    const isValid = getSchema(companyDefaultLanguage).isValidSync(formData);

    if (!isValid) {
      return toast.error(t('form.fillAllRequiredFields'));
    }
    try {
      const {
        clientCost,
        clientVat,
        cost,
        imageUrl,
        position,
        stock,
        volume,
        weight,
        requiredAddon,
        ...restFormData
      } = formData;

      const data = {
        ...trimSpaces(restFormData),
        requiredAddons: requiredAddon ? [requiredAddon] : [],
        clientCost: parseFloat(clientCost),
        clientVat: parseFloat(clientVat),
        cost: parseFloat(cost),
        // eslint-disable-next-line radix -- FIXME
        position: parseInt(position),
        // eslint-disable-next-line radix -- FIXME
        stock: parseInt(stock),
        volume: parseFloat(volume),
        weight: parseFloat(weight),
        seo: {
          title: addonSubpageData.metaTitle,
          description: addonSubpageData.metaDescription,
        },
        pageSettings: {
          slug: addonSubpageData.urlSlug,
          content: addonDescription,
        },
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- FIXME
      isEdit
        ? await put(`/addons/${addonId}`, data)
        : await post('/addons', data);

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- FIXME
      isEdit ? history.push('/admin/addons') : setFormData(initialFormData);

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- FIXME
      !isEdit && toast.success(t('status.success.save'));

      history.push('/admin/addons');
    } catch ({ response: { data = {}, status } }) {
      if (status === 500) {
        return toast.error(
          `${t('status.error.serverNotResponding')} ${t(
            'status.error.tryAgainLater'
          )}`
        );
      }

      toast.error(
        status === 400 && data?.violations
          ? `${t('status.error.somethingWentWrong')} ${t(
              'status.error.fillCorrectForm'
              // eslint-disable-next-line no-unsafe-optional-chaining -- FIXME
            )} ${(data?.violations).map(
              ({ message, propertyPath }) => `${propertyPath}: ${message}`
            )}`
          : `${t('status.error.somethingWentWrong')} ${t(
              'status.error.fillCorrectForm'
            )}`
      );
    }
  };

  return (
    <>
      <NavTabs
        key={`addon-tabs-${addonType}`}
        title={
          isEdit
            ? t('views.addons.addons.editAddon')
            : t('views.addons.addons.newAddon')
        }
        tabs={[
          {
            tabButton: t('common.details'),
            tabContent: (
              <ClientPanel
                formData={formData}
                handleChange={handleChange}
                handleCheckboxClick={handleCheckboxClick}
                categories={categories}
                getImage={getImage}
                removeImage={removeImage}
                contractors={contractors}
                diets={diets}
                subAddons={subAddons}
                addonType={addonType}
                isAddonTypeDisabled={isEdit}
                canEditClientFields={canEditClientFields}
                canSelectRequiredAddons={canSelectRequiredAddons}
              />
            ),
          },
          {
            tabButton: t('views.addons.addonSubpage'),
            disabled: isSubAddon,
            tabContent: (
              <>
                <BasicInfoCard
                  urlSlug={addonSubpageData.urlSlug}
                  metaTitle={addonSubpageData.metaTitle}
                  metaDescription={addonSubpageData.metaDescription}
                  handleChange={handleAddonSubpageInputChange}
                  subpage="/addons/"
                />
                <DescriptionCard
                  value={addonDescription}
                  setValue={setAddonDescription}
                  title={t('views.addons.addonSubpage.addonDescription')}
                />
              </>
            ),
          },
        ]}
      />

      <FormControlButtons
        discardText={t('form.cancel')}
        submitText={t('form.save')}
        cancelPath="/admin/addons"
        handleSubmit={handleSubmit}
      />
    </>
  );
};

export default AddonsForm;
